import { useState } from "react";
import {
  Box,
  Container,
  Stack,
  IconButton,
  Drawer
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Link, useLocation } from "react-router-dom";
import logo from "../static/logo.webp";

function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const DARK_GREEN = "#052e16";
  const GREEN = "#064e2a";
  const MID_GREEN = "#15803d";
  const SOFT_GREEN = "#f4fbf6";
  const LIGHT_GREEN = "#e8f5ec";

  const navItems = [
    { label: "Inicio", path: "/" },
    { label: "Calendario", path: "/calendario-de-partidos" },
    { label: "Galería", path: "/galeria", cta: true }
  ];

  const isActive = path => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <Box
      sx={{
        width: "100%",
        background: "linear-gradient(180deg, #ffffff 0%, #f4fbf6 100%)",
        borderBottom: "1px solid rgba(6,78,42,0.12)",
        boxShadow: "0 8px 24px rgba(6,78,42,0.08)",
        position: "relative",
        zIndex: 20
      }}
    >
      <Container
        sx={{
          height: 84,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}
      >
        <Box
          component={Link}
          to="/"
          sx={{
            display: "flex",
            alignItems: "center",
            textDecoration: "none"
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Liga Deportiva Cantonal Mejía"
            sx={{
              height: { xs: 56, md: 72 },
              filter: "drop-shadow(0 8px 14px rgba(6,78,42,0.14))"
            }}
          />
        </Box>

        <Stack
          direction="row"
          spacing={3}
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center"
          }}
        >
          {navItems.map(item => {
            const active = isActive(item.path);

            if (item.cta) {
              return (
                <Box
                  key={item.path}
                  component={Link}
                  to={item.path}
                  sx={{
                    px: 3,
                    py: 1.2,
                    borderRadius: "999px",
                    fontWeight: 900,
                    fontSize: ".95rem",
                    textDecoration: "none",
                    backgroundColor: active ? DARK_GREEN : GREEN,
                    color: "#fff",
                    transition: ".25s",
                    boxShadow: active
                      ? "0 0 0 3px rgba(21,128,61,0.22), 0 10px 24px rgba(6,78,42,0.24)"
                      : "0 10px 24px rgba(6,78,42,0.22)",
                    "&:hover": {
                      backgroundColor: DARK_GREEN,
                      transform: "scale(1.06)",
                      boxShadow: "0 14px 30px rgba(6,78,42,0.32)"
                    }
                  }}
                >
                  {item.label}
                </Box>
              );
            }

            return (
              <Box
                key={item.path}
                component={Link}
                to={item.path}
                sx={{
                  textDecoration: "none",
                  fontWeight: 900,
                  fontSize: ".95rem",
                  color: active ? GREEN : DARK_GREEN,
                  position: "relative",
                  transition: ".2s",
                  px: 0.4,
                  py: 0.6,
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: -4,
                    height: 3,
                    borderRadius: "999px",
                    backgroundColor: MID_GREEN,
                    transform: active ? "scaleX(1)" : "scaleX(0)",
                    transformOrigin: "center",
                    transition: "transform .22s ease"
                  },
                  "&:hover": {
                    color: GREEN
                  },
                  "&:hover::after": {
                    transform: "scaleX(1)"
                  }
                }}
              >
                {item.label}
              </Box>
            );
          })}
        </Stack>

        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            display: { xs: "flex", md: "none" },
            color: GREEN,
            backgroundColor: LIGHT_GREEN,
            border: "1px solid rgba(6,78,42,0.14)",
            "&:hover": {
              backgroundColor: SOFT_GREEN
            }
          }}
        >
          <MenuRoundedIcon />
        </IconButton>
      </Container>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            background: "linear-gradient(180deg, #ffffff 0%, #f4fbf6 100%)"
          }
        }}
      >
        <Box
          sx={{
            width: 280,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            px: 3,
            pt: 2
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <IconButton
              onClick={() => setOpen(false)}
              sx={{
                color: GREEN,
                backgroundColor: LIGHT_GREEN,
                border: "1px solid rgba(6,78,42,0.14)",
                "&:hover": {
                  backgroundColor: SOFT_GREEN
                }
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Box>

          <Box
            component="img"
            src={logo}
            alt="Liga Deportiva Cantonal Mejía"
            sx={{
              height: 72,
              width: "fit-content",
              mx: "auto",
              mt: 2,
              mb: 3,
              filter: "drop-shadow(0 8px 14px rgba(6,78,42,0.14))"
            }}
          />

          <Stack spacing={2.2} sx={{ mt: 1 }}>
            {navItems.map(item => {
              const active = isActive(item.path);

              if (item.cta) {
                return (
                  <Box
                    key={item.path}
                    component={Link}
                    to={item.path}
                    onClick={() => setOpen(false)}
                    sx={{
                      textDecoration: "none",
                      fontWeight: 900,
                      fontSize: "1.05rem",
                      textAlign: "center",
                      py: 1.35,
                      borderRadius: "999px",
                      backgroundColor: active ? DARK_GREEN : GREEN,
                      color: "#fff",
                      boxShadow: active
                        ? "0 0 0 3px rgba(21,128,61,0.22), 0 10px 24px rgba(6,78,42,0.24)"
                        : "0 10px 24px rgba(6,78,42,0.22)"
                    }}
                  >
                    {item.label}
                  </Box>
                );
              }

              return (
                <Box
                  key={item.path}
                  component={Link}
                  to={item.path}
                  onClick={() => setOpen(false)}
                  sx={{
                    textDecoration: "none",
                    fontWeight: 900,
                    fontSize: "1.05rem",
                    color: active ? GREEN : DARK_GREEN,
                    backgroundColor: active ? LIGHT_GREEN : "#fff",
                    border: active
                      ? "1px solid rgba(6,78,42,0.18)"
                      : "1px solid rgba(6,78,42,0.08)",
                    borderRadius: "14px",
                    px: 2,
                    py: 1.25,
                    boxShadow: active
                      ? "0 10px 24px rgba(6,78,42,0.10)"
                      : "none"
                  }}
                >
                  {item.label}
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Drawer>
    </Box>
  );
}

export default Navbar;